import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { Telemetry } from '@/types/api';
import { resultantG, shortTime } from '@/lib/format';

/** The physical gate a crash must clear before the severity engine is consulted. */
const CRASH_THRESHOLD_G = 3.5;

/**
 * Impact force over time, with the crash threshold drawn in.
 *
 * Resultant g rather than the three raw axes: it is what the detector actually
 * thresholds, and unlike per-axis values it does not change meaning when the device
 * is mounted at a different angle.
 */
export function TelemetryChart({ rows }: { rows: Telemetry[] }) {
  const data = useMemo(
    () =>
      rows
        .slice()
        // Sort explicitly rather than trusting the endpoint's ordering — a chart
        // drawn backwards puts the impact spike on the wrong side of the timeline.
        .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
        .map((row) => ({
          time: shortTime(row.timestamp),
          g: Number(resultantG(row.accel_x, row.accel_y, row.accel_z).toFixed(2)),
          speed: row.speed ?? 0,
        })),
    [rows]
  );

  const peak = data.reduce((max, point) => Math.max(max, point.g), 0);
  const upperBound = Math.max(CRASH_THRESHOLD_G * 1.3, peak * 1.15);

  return (
    <div className="h-56 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
          <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="time"
            tick={{ fill: '#94a3b8', fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: '#e2e8f0' }}
            minTickGap={40}
          />
          <YAxis
            domain={[0, Number(upperBound.toFixed(1))]}
            tick={{ fill: '#94a3b8', fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={40}
          />
          <Tooltip
            contentStyle={{
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              fontSize: 13,
              boxShadow: 'none',
            }}
            labelStyle={{ color: '#475569' }}
            formatter={(value: number) => [`${value} g`, 'Impact']}
          />
          <ReferenceLine
            y={CRASH_THRESHOLD_G}
            stroke="#b91c1c"
            strokeDasharray="4 4"
            label={{ value: 'Crash threshold', fill: '#b91c1c', fontSize: 11, position: 'insideTopRight' }}
          />
          <Line
            type="monotone"
            dataKey="g"
            stroke="#2563eb"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
